import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';

const buttonVariants = cva(
  /*
    El oficio del movimiento (spec 156 §6, plan 175 · B2 y B3).

    La transición NOMBRA lo que cambia: `transition-all` obliga al navegador a
    observar cada propiedad y anima de rebote cualquier cambio de layout —
    un ancho que se recalcula termina deslizándose sin que nadie lo pidiera.

    `active:scale-[.97]` va por variante y no acá, porque un enlace no es una
    superficie que se pueda hundir. Los deshabilitados ya quedan fuera por
    `disabled:pointer-events-none`, que impide el estado `:active`.
  */
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-boton text-sm font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-snap ease-snap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90 active:scale-[.97]',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 active:scale-[.97]',
        outline:
          'border border-input bg-campo shadow-sm hover:bg-accent hover:text-accent-foreground active:scale-[.97]',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 active:scale-[.97]',
        ghost: 'hover:bg-accent hover:text-accent-foreground active:scale-[.97]',
        // Una acción de marca que no es la principal («Revisar 35 seguros»).
        subtle: 'bg-primary/10 text-primary hover:bg-primary/15 active:scale-[.97]',
        /*
          La acción principal de la pantalla, con el degradado de Prixia (rediseño
          del 18-oct, artefacto): «Invitar usuario», «Nuevo equipo». Una por
          pantalla. Es el `BOTON_DEGRADADO` de Compite, ahora para toda la app.
        */
        degradado:
          'border-0 bg-degradado-prixia font-semibold text-primary-foreground shadow-md hover:shadow-lg hover:brightness-110 active:scale-[.97] dark:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      /*
        Vault 349: en el teléfono (bajo `md`) ningún botón mide menos de 40 px de blanco de
        toque. Va en px: la raíz de la app es de 14 px y `h-10` mide 35. En la PC no cambia.
      */
      size: {
        xs: 'h-7 rounded-boton px-2.5 text-xs max-md:min-h-[40px]',
        default: 'h-10 px-4 py-2 max-md:min-h-[40px]',
        sm: 'h-9 rounded-boton px-3 text-xs max-md:min-h-[40px]',
        lg: 'h-11 rounded-boton px-8 max-md:min-h-[44px]',
        icon: 'h-10 w-10 max-md:min-h-[40px] max-md:min-w-[40px]',
        'icon-sm': 'h-8 w-8 max-md:min-h-[40px] max-md:min-w-[40px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, children, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Cargando...</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
